import { io, Socket } from 'socket.io-client';
import { appEnv } from '../config/env';
import store from '@/stores';
import { disconnected } from '@/stores/receiver';
import { refreshTokensOnce } from './api';

const SESSION_ID_UNKNOWN = 'Session ID unknown';
const MAX_AUTH_RECOVERY_ATTEMPTS = 3;
const MIN_CONNECT_INTERVAL_MS = 500;
const RECONNECT_FAILED_FALLBACK_DELAY_MS = 3000;
const STALE_SESSION_RECONNECT_DELAY_MS = 50;

const AUTH_ERROR_PATTERNS = [
  'unauthorized',
  'missing token',
  'forbidden',
  'jwt expired',
  'jwt malformed',
  'invalid token',
  'token expired',
  'authentication error',
] as const;

type ConnectOptions = {
  force?: boolean;
  /** ゲスト参加時の招待 JWT（handshake query.invite） */
  invite?: string;
};

type SocketReplacedListener = (socket: Socket) => void;

export class Websocket {
  private static _socket: Socket | undefined;
  private static _socketGeneration = 0;
  private static _socketReplacedListeners = new Set<SocketReplacedListener>();
  private static _recoveryInProgress = false;
  private static _authRecoveryAttempts = 0;
  private static _connectInProgress = false;
  private static _lastConnectAt = 0;
  private static _browserRecoveryBound = false;
  /** ゲスト接続時の invite を force reconnect 後も引き継ぐ */
  private static _guestInviteToken: string | undefined;

  static clearGuestInvite(): void {
    this._guestInviteToken = undefined;
  }

  static connect(options: ConnectOptions = {}): Socket {
    const { force = false, invite } = options;
    if (invite) {
      this._guestInviteToken = invite;
    }
    const queryInvite = this._guestInviteToken;
    const existing = this._socket;

    if (existing?.connected && !force) {
      return existing;
    }

    if (existing && !force && this.isConnectionInProgress(existing)) {
      return existing;
    }

    if (
      !force &&
      existing &&
      Date.now() - this._lastConnectAt < MIN_CONNECT_INTERVAL_MS
    ) {
      return existing;
    }

    if (existing) {
      store.dispatch(disconnected());
      existing.removeAllListeners();
      existing.disconnect();
    }

    this._connectInProgress = true;
    this._lastConnectAt = Date.now();
    this._socket = io(appEnv.websocketUrl, {
      path: '/ws',
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      randomizationFactor: 0.5,
      ...(queryInvite ? { query: { invite: queryInvite } } : {}),
    });

    this.bindRecoveryHandlers(this._socket);
    this.bindConnectLifecycleHandlers(this._socket);
    this.bindBrowserRecoveryHandlers();
    this.exposeForE2E(this._socket);
    this.notifySocketReplaced(this._socket);

    return this._socket;
  }

  /**
   * E2E（Playwright）が WebSocket transport 昇格後に getInfo を要求・観測するための hook。
   * 開発ビルドかつ VITE_ENABLE_E2E_SOCKET_HOOK=true のときだけ公開する。
   */
  private static exposeForE2E(socket: Socket): void {
    if (
      !import.meta.env.DEV ||
      !appEnv.e2eSocketHookEnabled ||
      typeof window === 'undefined'
    ) {
      return;
    }
    (window as unknown as { __roomiWebsocket?: Socket }).__roomiWebsocket = socket;
    (
      window as unknown as {
        __roomiWebsocketE2E?: { setAuthRecoveryAttempts: (attempts: number) => void };
      }
    ).__roomiWebsocketE2E = {
      setAuthRecoveryAttempts: (attempts: number) => {
        this._authRecoveryAttempts = attempts;
      },
    };
  }

  static get socket(): Socket | undefined {
    return this._socket;
  }

  /** force reconnect で socket インスタンスが差し替わるたびに増える */
  static get socketGeneration(): number {
    return this._socketGeneration;
  }

  /**
   * connect({ force: true }) 等で新しい socket に差し替わったとき、
   * App 層（WebSocket.tsx / useSocketEvent）がリスナーを再登録するための通知。
   */
  static onSocketReplaced(listener: SocketReplacedListener): () => void {
    this._socketReplacedListeners.add(listener);
    return () => {
      this._socketReplacedListeners.delete(listener);
    };
  }

  private static notifySocketReplaced(socket: Socket): void {
    this._socketGeneration += 1;
    for (const listener of this._socketReplacedListeners) {
      listener(socket);
    }
  }

  private static isConnectionInProgress(socket: Socket): boolean {
    return this._connectInProgress || (!socket.connected && socket.active);
  }

  private static bindConnectLifecycleHandlers(socket: Socket): void {
    socket.off('connect', this.handleConnectSettled);
    socket.on('connect', this.handleConnectSettled);

    socket.off('connect_error', this.handleConnectSettled);
    socket.on('connect_error', this.handleConnectSettled);
  }

  private static handleConnectSettled = (): void => {
    this._connectInProgress = false;
  };

  private static bindRecoveryHandlers(socket: Socket): void {
    socket.off('connect_error', this.handleRecoveryConnectError);
    socket.on('connect_error', this.handleRecoveryConnectError);

    socket.io.off('reconnect_failed');
    socket.io.on('reconnect_failed', this.handleReconnectFailed);
  }

  private static handleRecoveryConnectError = (err: Error): void => {
    void this.handleConnectError(err);
  };

  private static handleReconnectFailed = (): void => {
    window.setTimeout(() => {
      if (!this._socket?.connected) {
        this.connect({ force: true });
      }
    }, RECONNECT_FAILED_FALLBACK_DELAY_MS);
  };

  private static bindBrowserRecoveryHandlers(): void {
    if (this._browserRecoveryBound || typeof window === 'undefined') {
      return;
    }
    this._browserRecoveryBound = true;

    const tryReconnect = (): void => {
      const socket = this._socket;
      if (!socket || socket.connected) {
        return;
      }
      // タブ復帰・オンライン復帰のたびに諦めバジェットをリセット（夜間に使い切っても翌朝再試行）
      this._authRecoveryAttempts = 0;
      this._recoveryInProgress = false;
      // 再接続処理が停止済みなら stale sid を避けてクリーンに張り直す
      if (!socket.active) {
        this.connect({ force: true });
      } else {
        socket.connect();
      }
    };

    window.addEventListener('online', tryReconnect);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        tryReconnect();
      }
    });
  }

  /** サーバー側切断時に App 層の disconnect ハンドラから呼ぶ */
  static recoverAfterServerDisconnect(): void {
    void this.tryAuthRefreshAndReconnect();
  }

  private static async handleConnectError(err: Error): Promise<void> {
    if (this._recoveryInProgress) {
      return;
    }
    const msg = err?.message ?? '';

    if (msg.includes(SESSION_ID_UNKNOWN)) {
      this.recoverFromStaleSession();
      return;
    }

    if (this.isLikelyAuthError(msg)) {
      await this.tryAuthRefreshAndReconnect();
    }
  }

  /**
   * サーバー側で session が既に破棄された場合（Session ID unknown）に、
   * 古い sid 付きの再接続ループを止めて新規 handshake へ切り替える。
   * disconnect() はアップグレード中 WebSocket を中断するため、
   * ブラウザコンソールに "WebSocket is closed before the connection is established"
   * が出ることがある（機能上は問題なく、bindBrowserRecoveryHandlers 側の
   * force reconnect でも同種の警告は抑制される）。
   */
  private static recoverFromStaleSession(): void {
    if (this._recoveryInProgress || !this._socket) {
      return;
    }
    if (this._socket.connected) {
      return;
    }
    this._recoveryInProgress = true;
    const socket = this._socket;
    socket.io.opts.reconnection = false;
    socket.disconnect();
    window.setTimeout(() => {
      this._recoveryInProgress = false;
      this.connect({ force: true });
    }, STALE_SESSION_RECONNECT_DELAY_MS);
  }

  private static isLikelyAuthError(message: string): boolean {
    const lower = message.toLowerCase();
    return AUTH_ERROR_PATTERNS.some((pattern) => lower.includes(pattern));
  }

  private static async tryAuthRefreshAndReconnect(): Promise<void> {
    if (this._recoveryInProgress || !this._socket) {
      return;
    }
    if (this._socket.connected) {
      return;
    }
    if (this._authRecoveryAttempts >= MAX_AUTH_RECOVERY_ATTEMPTS) {
      return;
    }
    this._recoveryInProgress = true;
    this._authRecoveryAttempts += 1;
    try {
      const result = await refreshTokensOnce({ bypassCooldown: true });
      if (result.ok) {
        this._authRecoveryAttempts = 0;
        this.connect({ force: true });
      }
    } finally {
      this._recoveryInProgress = false;
    }
  }
}
